/**
 * InitiativesPage — J6
 *
 * Kanban-lite portfolio of org initiatives with five columns mapping to the
 * status enum on `roros_initiatives`:
 *
 *   - planned                 ("Proposed")
 *   - in_progress             ("Active")
 *   - done                    ("Done")
 *   - converted_to_routine    ("Converted")
 *   - stopped                 ("Stopped")
 *
 * Features:
 *   - "+ New Initiative" FormSheet (Title, Domain, Start, End, Description)
 *   - Edit initiative FormSheet (same fields) — Wave 4a addition
 *   - "Convert to Routine" action on each card (J6 backend addition)
 *   - "Simultaneous development view" — initiatives grouped by `domain` so the
 *     CEO can see what is being developed in parallel across domains.
 *
 * PRD §9.3.6 + §12.3.9 J6 frontend page #1.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQueries } from '@tanstack/react-query';
import { Rocket, Plus, ArrowRightCircle, Pencil } from 'lucide-react';
import { RorosPage } from '@/components/roros/RorosPage';
import { Card } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Alert } from '@/components/ui/Alert';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { showToast } from '@/components/ui/Toast';
import { t } from '@/lib/i18n';
import { useRorosDomains } from '@/hooks/roros/domains';
import {
  useRorosInitiativeConvertToRoutine,
} from '@/hooks/roros/artifacts';
import { useFormSheetUrlRouting } from '@/hooks/useFormSheetUrlRouting';
import { rorosKeys } from '@/hooks/roros/keys';
import { RorosArtifactService } from '@/services/RorosService';
import type { RorosInitiativeDTO } from '@/types/roros/artifacts';
import type { RorosDomainDTO } from '@/types/roros/domains';
import { usePermissions } from '@/hooks/usePermissions';
import { PERMISSIONS } from '@/constants/permissions';
import { InitiativeFormDialog } from './InitiativeFormDialog';

// ─── Constants ────────────────────────────────────────────────────────────────

const INITIATIVE_URL_KEY = 'edit-initiative';

// ─── Column definitions ──────────────────────────────────────────────────────

type ColumnKey = 'planned' | 'in_progress' | 'done' | 'converted_to_routine' | 'stopped';

interface ColumnDef {
  key: ColumnKey;
  /** i18n label rendered as column header */
  label: string;
  badgeVariant: 'default' | 'primary' | 'success' | 'warning';
}

const COLUMNS: ColumnDef[] = [
  { key: 'planned', label: t('Proposed'), badgeVariant: 'default' },
  { key: 'in_progress', label: t('Active'), badgeVariant: 'primary' },
  { key: 'done', label: t('Done'), badgeVariant: 'success' },
  { key: 'converted_to_routine', label: t('Converted'), badgeVariant: 'success' },
  { key: 'stopped', label: t('Stopped'), badgeVariant: 'warning' },
];

// ─── Helpers ─────────────────────────────────────────────────────────────────

function statusToColumn(status: string): ColumnKey | null {
  if (status === 'planned' || status === 'in_progress' || status === 'done' || status === 'converted_to_routine' || status === 'stopped') {
    return status;
  }
  return null; // archived → not shown on board
}

function resolveDomainName(domains: RorosDomainDTO[], id: string): string {
  return domains.find((d) => d.id === id)?.name ?? id;
}

// ─── Initiative Card ─────────────────────────────────────────────────────────

interface InitiativeCardProps {
  initiative: RorosInitiativeDTO;
  domainName: string;
  canEdit: boolean;
  onEdit: (initiative: RorosInitiativeDTO) => void;
  onConvert: (initiative: RorosInitiativeDTO) => void;
  isConverting: boolean;
}

function InitiativeCard({ initiative, domainName, canEdit, onEdit, onConvert, isConverting }: InitiativeCardProps) {
  const canConvert =
    !initiative.recurrencePattern &&
    initiative.status !== 'converted_to_routine' &&
    initiative.status !== 'stopped' &&
    !initiative.convertToRoutineId;
  return (
    <Card
      padding="sm"
      className="border border-border bg-card text-sm space-y-2"
      data-testid={`initiative-card-${initiative.id}`}
    >
      <div className="flex items-start justify-between gap-1">
        <div className="font-medium text-foreground line-clamp-2 flex-1" title={initiative.title}>
          {initiative.title}
        </div>
        {canEdit && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => onEdit(initiative)}
            aria-label={t('Edit initiative')}
            data-testid={`initiative-edit-${initiative.id}`}
            className="flex-shrink-0 -mt-1 -mr-1 h-7 w-7 p-0"
          >
            <Pencil className="w-3.5 h-3.5" aria-hidden />
          </Button>
        )}
      </div>
      <div className="text-xs text-muted-foreground flex flex-wrap items-center gap-2">
        <Badge variant="default" className="text-[10px]">{domainName}</Badge>
        {initiative.recurrencePattern && (
          <Badge variant="primary" className="text-[10px]">
            {t('recurring')} · {initiative.recurrencePattern}
          </Badge>
        )}
      </div>
      <div className="text-xs text-muted-foreground">
        {initiative.startDate} → {initiative.endDate}
      </div>
      {canConvert && (
        <Button
          type="button"
          size="sm"
          variant="outline"
          isLoading={isConverting}
          onClick={() => onConvert(initiative)}
          data-testid={`initiative-convert-${initiative.id}`}
          className="w-full"
        >
          <ArrowRightCircle className="w-4 h-4 mr-1.5" aria-hidden />
          {t('Convert to Routine')}
        </Button>
      )}
      {initiative.convertToRoutineId && (
        <div className="text-xs text-success-600 flex items-center gap-1">
          <ArrowRightCircle className="w-3 h-3" aria-hidden />
          {t('Linked to Routine')}
        </div>
      )}
    </Card>
  );
}

// ─── Main page ──────────────────────────────────────────────────────────────

export function InitiativesPage() {
  const { hasPermission } = usePermissions();
  const canCreate = hasPermission(PERMISSIONS.ROROS_ARTIFACTS_CREATE);
  const canEdit = hasPermission(PERMISSIONS.ROROS_ARTIFACTS_EDIT);

  const domainsQuery = useRorosDomains();
  const domains = (domainsQuery.data ?? []) as RorosDomainDTO[];
  const activeDomains = useMemo(() => domains.filter((d) => d.isActive), [domains]);

  // Fetch initiatives across every active domain. We use `useQueries` rather
  // than calling a hook in a `.map()` loop (which would violate the rules of
  // hooks). The number of queries varies between renders only when the set of
  // active domains itself changes — useQueries handles that correctly.
  const initiativesQueries = useQueries({
    queries: activeDomains.map((d) => ({
      queryKey: rorosKeys.initiatives.byDomain(d.id),
      queryFn: () => RorosArtifactService.listInitiatives(d.id),
      enabled: !!d.id,
      staleTime: 2 * 60_000,
      gcTime: 5 * 60_000,
    })),
  });
  const isLoading = initiativesQueries.some((q) => q.isLoading);
  const isError = initiativesQueries.some((q) => q.isError);
  const errorMsg = initiativesQueries.find((q) => q.isError)?.error?.message;

  const initiatives = useMemo<RorosInitiativeDTO[]>(
    () => initiativesQueries.flatMap((q) => (q.data ?? []) as RorosInitiativeDTO[]),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [JSON.stringify(initiativesQueries.map((q) => q.dataUpdatedAt))],
  );

  const initiativesByColumn = useMemo(() => {
    const map: Record<ColumnKey, RorosInitiativeDTO[]> = {
      planned: [],
      in_progress: [],
      done: [],
      converted_to_routine: [],
      stopped: [],
    };
    for (const init of initiatives) {
      const col = statusToColumn(init.status);
      if (col) map[col].push(init);
    }
    return map;
  }, [initiatives]);

  // Simultaneous-development view: group active initiatives by domain.
  const simultaneousByDomain = useMemo(() => {
    const map = new Map<string, RorosInitiativeDTO[]>();
    for (const init of initiatives) {
      if (init.status !== 'in_progress') continue;
      const arr = map.get(init.domainId) ?? [];
      arr.push(init);
      map.set(init.domainId, arr);
    }
    return Array.from(map.entries())
      .map(([domainId, list]) => ({
        domainId,
        domainName: resolveDomainName(domains, domainId),
        list,
      }))
      .sort((a, b) => a.domainName.localeCompare(b.domainName));
  }, [initiatives, domains]);

  // ─── Form sheet state ────────────────────────────────────────────────────
  const [formOpen, setFormOpen] = useState(false);
  const [editingInitiative, setEditingInitiative] = useState<RorosInitiativeDTO | null>(null);

  const openCreate = useCallback(() => {
    setEditingInitiative(null);
    setFormOpen(true);
  }, []);

  const openEdit = useCallback((initiative: RorosInitiativeDTO) => {
    setEditingInitiative(initiative);
    setFormOpen(true);
  }, []);

  // ─── URL routing (?edit-initiative=<id>) ─────────────────────────────────
  const getInitiativeId = useCallback((i: RorosInitiativeDTO) => i.id, []);
  const { deepLinkTarget, consumeDeepLink } = useFormSheetUrlRouting({
    urlKey: INITIATIVE_URL_KEY,
    items: initiatives,
    getId: getInitiativeId,
  });

  useEffect(() => {
    if (!deepLinkTarget) return;
    setEditingInitiative(deepLinkTarget);
    setFormOpen(true);
    consumeDeepLink();
  }, [deepLinkTarget, consumeDeepLink]);

  // ─── Convert flow ─────────────────────────────────────────────────────────
  const [confirmConvert, setConfirmConvert] = useState<RorosInitiativeDTO | null>(null);
  const convertMutation = useRorosInitiativeConvertToRoutine();

  const onConvertConfirmed = () => {
    if (!confirmConvert) return;
    const target = confirmConvert;
    convertMutation.mutate(
      { domainId: target.domainId, id: target.id },
      {
        onSuccess: () => {
          showToast.success(t('Routine created from initiative'));
          setConfirmConvert(null);
        },
        onError: (err: any) => {
          showToast.error(err?.message ?? t('Failed to convert initiative'));
        },
      },
    );
  };

  return (
    <RorosPage
      title={t('Initiatives Portfolio')}
      icon={<Rocket className="w-5 h-5" />}
      breadcrumbs={[{ label: t('Initiatives'), path: '/roros/artifacts/initiatives' }]}
      actions={
        canCreate ? (
          <Button
            variant="primary"
            onClick={openCreate}
            disabled={activeDomains.length === 0}
            data-testid="initiative-new-button"
          >
            <Plus className="w-4 h-4 mr-1.5" aria-hidden />
            {t('New Initiative')}
          </Button>
        ) : undefined
      }
    >
      {isError && (
        <Alert variant="error" title={t('Error loading initiatives')} className="mb-4">
          {errorMsg ?? t('Failed to load initiatives')}
        </Alert>
      )}

      {/* Empty domains hint */}
      {!isLoading && activeDomains.length === 0 && (
        <Alert variant="info" title={t('No active domains')} className="mb-4">
          {t('Activate at least one domain in Structure Settings before creating initiatives.')}
        </Alert>
      )}

      {/* Kanban-lite grid */}
      <div
        className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-3"
        role="grid"
        aria-label={t('Initiatives Board')}
        data-testid="initiatives-board"
      >
        {COLUMNS.map((col) => {
          const cards = initiativesByColumn[col.key];
          return (
            <div
              key={col.key}
              role="gridcell"
              aria-label={col.label}
              className="bg-page-bg border border-border rounded-lg p-3 min-h-[200px] flex flex-col"
              data-testid={`initiative-column-${col.key}`}
            >
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-semibold text-foreground">{col.label}</h3>
                <Badge variant={col.badgeVariant} className="text-xs">{cards.length}</Badge>
              </div>
              <div className="space-y-2 flex-1">
                {cards.length === 0 ? (
                  <p className="text-xs text-muted-foreground italic">{t('No initiatives')}</p>
                ) : (
                  cards.map((init) => (
                    <InitiativeCard
                      key={init.id}
                      initiative={init}
                      domainName={resolveDomainName(domains, init.domainId)}
                      canEdit={canEdit}
                      onEdit={openEdit}
                      onConvert={(i) => setConfirmConvert(i)}
                      isConverting={convertMutation.isPending && confirmConvert?.id === init.id}
                    />
                  ))
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Simultaneous-development view */}
      <section className="mt-8" aria-label={t('Simultaneous Development')} data-testid="simultaneous-development">
        <h2 className="text-lg font-semibold text-foreground mb-3 flex items-center gap-2">
          <Rocket className="w-5 h-5" aria-hidden />
          {t('Simultaneous Development')}
        </h2>
        <p className="text-sm text-muted-foreground mb-3">
          {t('Active initiatives grouped by domain — what is being developed in parallel right now.')}
        </p>
        {simultaneousByDomain.length === 0 ? (
          <Alert variant="info" title={t('No parallel development in flight')}>
            {t('Move initiatives into the Active column to see them grouped here.')}
          </Alert>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {simultaneousByDomain.map((group) => (
              <Card key={group.domainId} padding="md" className="space-y-2">
                <div className="flex items-center justify-between">
                  <Link
                    to={`/roros/domains/${group.domainId}`}
                    className="text-sm font-semibold text-primary-600 hover:underline"
                  >
                    {group.domainName}
                  </Link>
                  <Badge variant="primary" className="text-xs">{group.list.length}</Badge>
                </div>
                <ul className="space-y-1 text-xs">
                  {group.list.map((i) => (
                    <li key={i.id} className="text-foreground">
                      • {i.title}
                    </li>
                  ))}
                </ul>
              </Card>
            ))}
          </div>
        )}
      </section>

      {isLoading && (
        <p className="text-sm text-muted-foreground mt-3">{t('Loading initiatives…')}</p>
      )}

      {/* Create / Edit FormSheet */}
      <InitiativeFormDialog
        mode={editingInitiative ? 'edit' : 'create'}
        isOpen={formOpen}
        onClose={() => {
          setFormOpen(false);
          setEditingInitiative(null);
        }}
        domains={domains}
        initiative={editingInitiative ?? undefined}
        urlKey={editingInitiative ? INITIATIVE_URL_KEY : undefined}
        urlId={editingInitiative?.id}
      />

      <ConfirmDialog
        isOpen={!!confirmConvert}
        onClose={() => setConfirmConvert(null)}
        onConfirm={onConvertConfirmed}
        title={t('Convert Initiative to Routine?')}
        description={
          confirmConvert
            ? `${t('This will create a new active Routine from')} "${confirmConvert.title}". ${t('The initiative will be marked as converted.')}`
            : ''
        }
        confirmText={t('Convert')}
        cancelText={t('Cancel')}
        isLoading={convertMutation.isPending}
      />
    </RorosPage>
  );
}
